import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import { StyledJumbotron } from '../../components/shared/jumbotron/jumbotron-styled';
import moment from 'moment';
import {
  BaSeTheme,
  BaSeContainer,
  BaSeDialogContext,
  BaSeDataTable,
  BaSeActionItem,
} from '@base/react';
import {
  DataTableWrapper,
  PaddingContainer,
  ActionItemWrapper,
  StatusTagWrapper,
} from './certificados-cliente-styled';
import {
  CERTIFICADOS_ERROS_GRID_ENDPOINT,
  CERTIFICADO_ENDPOINT,
} from '../../hooks/constants';
import { Title } from '../../components/title/title';
import { DadosCertificado } from '../../components/dados-certificado/dados-certificado';
import { StatusCertificado } from '../../hooks/status-certificado';
import { VisualizarDetalheErro } from '../../components/visualizar-detalhe-erro/visualizar-detalhe-erro';
import { useCertificadosService } from '../../hooks/certificado';
import { Role } from '../../models/role';
import { avaliableActions } from '../../models/actions';
import { DeleteText } from '../../components/delete/delete-text';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CertificadosErroProcessamentoPage = () => {
  const [sendLoading, setSendLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const { isAuthenticated, isInitialized, hasRoles } = Sebrae.useAuth();
  const { backendDelete, backendPut } = Sebrae.useBackend();
  const { showNotification, showModal } = React.useContext(BaSeDialogContext);
  const { sendReprocessing, sendReprocessingAllError, deleteSelectedItems } =
    useCertificadosService();

  const { fetchData, data, loading, error } = Sebrae.usePagination(
    CERTIFICADOS_ERROS_GRID_ENDPOINT,
  );

  React.useEffect(() => {
    if (error) {
      showNotification({
        message: error.message,
        color: 'destructive',
      });
    }
  }, [error, isInitialized, isAuthenticated, showNotification]);

  const handleUpdate = (id) => async (params) => {
    try {
      await backendPut(`${CERTIFICADO_ENDPOINT}/${id}`, params);
      fetchData({ sort: { field: 'dataConclusao', type: 'DESC' } });
      return { sucess: true };
    } catch (e) {
      console.log(e);
      return { ...e };
    }
  };

  const modalConfigs = React.useCallback(
    (
      certificado,
      func,
      title,
      actions,
      content,
      size = 'large',
      isFluid = true,
      breakpointDelimiter = 'tablet',
    ) => ({
      size,
      title,
      actions,
      isFluid,
      breakpointDelimiter,
      onChooseAction: (action) => func?.(action, certificado),
      content: (onClose) => React.cloneElement(content, { onClose }),
    }),
    [],
  );

  const handleShowModal = (id) => {
    return modalConfigs(
      null,
      null,
      'Dados do certificado',
      [],
      <DadosCertificado id={id} isShow />,
    );
  };

  const handleUpdateModal = (id) => {
    return modalConfigs(
      null,
      null,
      'Editar dados do certificado',
      [],
      <DadosCertificado
        id={id}
        notify={showNotification}
        isUpdate
        save={handleUpdate(id)}
        openModal={showModal}
      />,
    );
  };

  const handleShowErroModal = (id) => {
    return modalConfigs(
      null,
      null,
      'Detalhe do erro',
      [],
      <VisualizarDetalheErro id={id} />,
    );
  };

  const handleDeleteAction = async (action, id) => {
    if (action === avaliableActions.confirmar) {
      try {
        await backendDelete(`${CERTIFICADO_ENDPOINT}/${id}`);
        showNotification({
          message: 'Registro excluído com sucesso!',
          color: 'confirmation',
        });
        fetchData({ sort: { field: 'dataCriacao', type: 'DESC' } });
        return { sucess: true };
      } catch (e) {
        console.log(e);
        return { ...e };
      }
    }
    return true;
  };

  const handleShowDeleteModal = (id) => {
    const deleteDescription = [
      'Tem certeza que deseja excluir esse certificado?',
      'Não será possível recuperar.',
    ];
    return modalConfigs(
      id,
      handleDeleteAction,
      'Atenção!',
      [
        avaliableActions.confirmar,
        {
          action: avaliableActions.desistir,
          type: 'secondary',
          color: BaSeTheme.colors.institucionais.azulSebrae36,
        },
      ],
      <DeleteText texts={deleteDescription} />,
      'medium',
      false,
    );
  };

  const handleDeleteSelectedItemsAction = async (
    action,
    selectedItems,
    isAllSelected,
    onSuccess,
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    if (action === avaliableActions.confirmar) {
      const idsCertificados = selectedItems.reduce((acc, { id }) => {
        acc.push(id);
        return acc;
      }, []);

      const ids = {
        ids: isAllSelected ? [] : idsCertificados,
        reprocessarTodos: isAllSelected ?? false,
      };

      isAllSelected
        ? await deleteSelectedItems(
            ids,
            onSuccess,
            () => setSendLoading(false),
            searchText,
          )
        : await deleteSelectedItems(
            ids,
            onSuccess,
            () => setSendLoading(false),
            searchText,
          );
      fetchData({ sort: { field: 'dataConclusao', type: 'DESC' } });
    }
    return true;
  };

  const modalConfigDelete = React.useCallback(
    (
      selecteItems,
      allSelected,
      reloadData,
      func,
      title,
      actions,
      content,
      size,
    ) => ({
      size,
      title,
      actions,
      beforeClose: (action) =>
        func?.(action, selecteItems, allSelected, reloadData),
      content: (onClose) => React.cloneElement(content, { onClose }),
    }),
    [],
  );

  const handleDeleteSelectedItems = (allSelected, selecteItems, reloadData) => {
    const pluralMessage = (selecteItems.length || data.total) > 1 ? 's' : '';

    const deleteDescription = [
      `Tem certeza que deseja excluir o${pluralMessage}${
        !!selecteItems.length
          ? pluralMessage === 's'
            ? ` ${selecteItems.length}`
            : ''
          : ` ${data.total}`
      } certificado${pluralMessage}?`,
      'Não será possível recuperar.',
    ];

    return modalConfigDelete(
      selecteItems,
      allSelected,
      reloadData,
      handleDeleteSelectedItemsAction,
      'Atenção!',
      [
        avaliableActions.confirmar,
        {
          action: avaliableActions.desistir,
          type: 'secondary',
          color: BaSeTheme.colors.institucionais.azulSebrae36,
        },
      ],
      <DeleteText texts={deleteDescription} />,
      'medium',
    );
  };

  const handleSendReprocessing = React.useCallback(
    async (isAllSelected, selectedItems, onSuccess) => {
      setSendLoading(true);

      const idsCertificados = !Array.isArray(selectedItems)
        ? [selectedItems]
        : selectedItems.reduce((acc, { id }) => {
            acc.push(id);
            return acc;
          }, []);

      const ids = {
        ids: idsCertificados,
        reprocessarTodos: false,
      };

      isAllSelected
        ? await sendReprocessingAllError(null, onSuccess, () =>
            setSendLoading(false),
          )
        : await sendReprocessing(ids, onSuccess, () => setSendLoading(false));
      fetchData({ sort: { field: 'dataCriacao', type: 'DESC' } });
    },
    [fetchData, sendReprocessing, sendReprocessingAllError],
  );

  return (
    <div>
      <StyledJumbotron
        backgroundColor={BaSeTheme.colors.institucionais.azulSebrae97}
      >
        <div>
          <Title
            titulo="Certificados Sebrae"
            subtitulo="Visualização de erros de processamento"
            paddingContent="16px"
            paddingSubtitulo="16px"
          />
        </div>
      </StyledJumbotron>
      <div style={{marginBotton: 20}}>
      <BaSeContainer delimitedOnBreakpoint="desktop">
        <PaddingContainer>
          <DataTableWrapper>
            <BaSeDataTable
              backgroundColor={BaSeTheme.colors.defaultColors.white}
              columnConfig={[
                {
                  columnId: 'id',
                  stickyOnCompactMode: true,
                },
              ]}
              config={[
                {
                  accessorKey: 'cpf',
                  header: 'CPF do Cliente',
                  mapper: ({ values }) => {
                    const cpf = values.cpf.replace(/\D/g, '');
                    return cpf.replace(
                      /(\d{3})(\d{3})(\d{3})(\d{2})/,
                      '$1.$2.$3-$4',
                    );
                  },
                },
                {
                  accessorKey: 'nome',
                  header: 'Nome do Cliente',
                },
                {
                  accessorKey: 'codigoProduto',
                  header: 'Código SAS',
                },
                {
                  accessorKey: 'nomeProduto',
                  header: 'Produto',
                  mapper: ({ values: { nomeProduto } }) => nomeProduto ?? '-',
                },
                {
                  accessorKey: 'dataSituacao',
                  header: 'Data da status',
                  mapper: ({ values: { dataSituacao } }) => {
                    return moment(dataSituacao).format('DD/MM/YYYY - HH:mm:ss');
                  },
                },
                {
                  accessorKey: 'situacao',
                  header: 'Status',
                  mapper: ({ values }) => {
                    return (
                      <StatusTagWrapper>
                        {StatusCertificado(values)}
                      </StatusTagWrapper>
                    );
                  },
                },
                {
                  header: 'Ações',
                  mapper: ({ values, reloadData }) => (
                    <ActionItemWrapper>
                      <BaSeActionItem
                        position="bottom-right"
                        size="small"
                        items={[
                          {
                            text: 'Vizualizar dados do certificado',
                            icon: 'eye',
                            color:
                              BaSeTheme.colors.institucionais.cinzaSebrae45,
                            callback: () =>
                              showModal(handleShowModal(values.id)),
                          },
                          {
                            text: 'Editar dados do certificado',
                            icon: 'pen',
                            color:
                              BaSeTheme.colors.institucionais.cinzaSebrae45,
                            callback: () =>
                              showModal(handleUpdateModal(values.id)),
                          },
                          {
                            text: 'Visualizar detalhe do erro',
                            icon: 'file-exclamation',
                            color:
                              BaSeTheme.colors.institucionais.cinzaSebrae45,
                            callback: () =>
                              showModal(handleShowErroModal(values.id)),
                          },
                          {
                            text: 'Enviar para reprocessamento',
                            icon: 'sync-exclamation',
                            color:
                              BaSeTheme.colors.institucionais.cinzaSebrae45,
                            callback: () =>
                              handleSendReprocessing(
                                false,
                                values.id,
                                reloadData,
                              ),
                          },
                          {
                            text: 'Excluir certificado',
                            icon: 'trash-alt',
                            color:
                              BaSeTheme.colors.institucionais.cinzaSebrae45,
                            callback: () =>
                              showModal(handleShowDeleteModal(values.id)),
                          },
                        ]}
                      />
                    </ActionItemWrapper>
                  ),
                },
              ]}
              selectionButtonActions={({
                isAllSelected,
                selectedItems,
                reloadData,
              }) => [
                ...(!!selectedItems.length &&
                hasRoles([Role.Administrador,
                  Role.CertificadoReprocessar,
                  Role.CertificadoReprocessarErros,
                ])
                  ? [
                      {
                        value: 'Reprocessar',
                        color: 'default',
                        type: 'primary',
                        isLoading: sendLoading,
                        onClick: () => {
                          handleSendReprocessing(
                            isAllSelected,
                            selectedItems,
                            reloadData,
                          );
                        },
                      },
                      {
                        value: 'Excluir',
                        color: 'destructive',
                        type: 'primary',
                        isLoading: sendLoading,
                        onClick: () => {
                          showModal(
                            handleDeleteSelectedItems(
                              isAllSelected,
                              selectedItems,
                              reloadData,
                            ),
                          );
                        },
                      },
                    ]
                  : isAllSelected &&
                    hasRoles([Role.Administrador,
                      Role.CertificadoReprocessar,
                      Role.CertificadoReprocessarErros,
                    ]) && [
                      {
                        value: 'Reprocessar todos',
                        color: 'default',
                        type: 'primary',
                        isLoading: sendLoading,
                        onClick: () => {
                          handleSendReprocessing(
                            isAllSelected,
                            selectedItems,
                            reloadData,
                          );
                        },
                      },
                      {
                        value: 'Excluir todos',
                        color: 'destructive',
                        type: 'primary',
                        isLoading: sendLoading,
                        onClick: () => {
                          showModal(
                            handleDeleteSelectedItems(
                              isAllSelected,
                              selectedItems,
                              reloadData,
                            ),
                          );
                        },
                      },
                    ]),
              ]}
              fetchData={fetchData}
              onTextFilter={({ setFilter, term }) => {
                setSearchText(term);
                setFilter({ filter: term });
              }}
              title="Certificados existentes para o CPF"
              titleColor={BaSeTheme.colors.institucionais.cinzaSebrae45}
              hasTextFilter
              hasPagination
              hasSelection
              hasSorting
              initialPageIndex={0}
              initialPageSize={10}
              initialSortField="dataCriacao"
              initialSortType="DESC"
              isLoading={loading}
              items={data.items}
              total={data.total}
              itemsPerPage={[10, 20, 30]}
            />
          </DataTableWrapper>
        </PaddingContainer>
      </BaSeContainer>
      </div>
    </div>
  );
};
